import React, { useEffect, useRef, useState } from 'react';
import db from '../../services/query';
import AccessDeniedResponse from '../common/AccessDeniedResponse';
import { EmptyState, LoadingState } from '../common/SurfaceUI';
import ParityReport, { allReportPages } from '../common/ParityReport';
import useParityAccess from '../common/useParityAccess';

const ReturnIcon = () => (
  <svg viewBox="0 0 18 18" aria-hidden="true">
    <path fill="currentColor" d="M 4.738281 1.777344 C 3.757813 1.777344 2.960938 2.570313 2.960938 3.550781 L 2.960938 14.210938 C 2.960938 14.367188 3.023438 14.519531 3.136719 14.628906 C 3.34375 15.378906 3.925781 15.988281 4.738281 15.988281 L 14.210938 15.988281 C 14.421875 15.988281 14.621094 15.878906 14.730469 15.691406 C 14.835938 15.507813 14.835938 15.28125 14.730469 15.097656 C 14.621094 14.910156 14.421875 14.800781 14.210938 14.800781 L 4.738281 14.800781 C 4.402344 14.800781 4.144531 14.546875 4.144531 14.210938 C 4.144531 13.875 4.402344 13.617188 4.738281 13.617188 L 13.617188 13.617188 C 14.273438 13.617188 14.800781 13.089844 14.800781 12.433594 L 14.800781 2.960938 C 14.800781 2.304688 14.273438 1.777344 13.617188 1.777344 Z M 8.882813 3.550781 C 9.207031 3.550781 9.472656 3.820313 9.472656 4.144531 C 9.472656 4.472656 9.207031 4.738281 8.882813 4.738281 C 8.554688 4.738281 8.289063 4.472656 8.289063 4.144531 C 8.289063 3.820313 8.554688 3.550781 8.882813 3.550781 Z M 8.882813 5.921875 C 9.207031 5.921875 9.472656 6.1875 9.472656 6.511719 L 9.472656 9.820313 L 10.238281 9.054688 C 10.46875 8.824219 10.84375 8.824219 11.078125 9.054688 C 11.308594 9.285156 11.308594 9.660156 11.078125 9.890625 L 9.300781 11.667969 C 9.246094 11.722656 9.179688 11.765625 9.109375 11.796875 C 9.035156 11.824219 8.957031 11.84375 8.882813 11.84375 C 8.804688 11.84375 8.726563 11.824219 8.65625 11.796875 C 8.582031 11.765625 8.519531 11.722656 8.464844 11.667969 L 6.6875 9.890625 C 6.457031 9.660156 6.457031 9.285156 6.6875 9.054688 C 6.917969 8.824219 7.292969 8.824219 7.523438 9.054688 L 8.289063 9.820313 L 8.289063 6.511719 C 8.289063 6.1875 8.554688 5.921875 8.882813 5.921875 Z" />
  </svg>
);

const columns = [
  { label: 'شماره', value: (_r, i) => i + 1 },
  { path: 'lender_name', label: 'نام' },
  { path: 'name', label: 'کتاب' },
  { path: 'registration_no', label: 'شماره ثبت' },
  { path: 'price', label: 'قیمت' },
  { path: 'lend_date', label: 'تاریخ امانت' },
  { path: 'lend_duration', label: 'مدت(روز)' },
  { path: 'days_left', label: 'مهلت(روز)' },
  { path: 'return_date', label: 'تاریخ برگشت' },
  { path: 'fine', label: 'جریمه' },
];

export default function BorrowedBooks() {
  const can = useParityAccess();
  const title = 'امانت گیرندگان';
  const [body, setBody] = useState({ data: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ orderBy: 'id', order: 'desc' });
  const [returningId, setReturningId] = useState(null);
  const [message, setMessage] = useState('');
  const generation = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => { setTerm(search); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const params = new URLSearchParams({ page: String(page), limit: '40', search: term, ...sort });
  const route = `mis-parity/books/loans?${params}`;

  const refresh = async () => {
    const current = ++generation.current;
    setLoading(true); setError(''); setDenied(false); setMessage('');
    try {
      const { data } = await db.getAll(route);
      if (current === generation.current) setBody(data);
    } catch (e) {
      if (current === generation.current) {
        setDenied(e?.response?.status === 403);
        setError('بارگیری ناموفق بود.');
      }
    } finally {
      if (current === generation.current) setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    return () => { generation.current += 1; };
  }, [route]);

  const returnBook = async (loan) => {
    if (Number(loan.returned) === 1) return;
    setReturningId(loan.id); setMessage('');
    try {
      await db.put(`mis-parity/books/loans/${loan.id}/return`, {});
      await refresh();
    } catch (_) {
      setMessage('ثبت برگشت ناموفق بود.');
    } finally {
      setReturningId(null);
    }
  };

  return (
    <div className="main-content mis-surface parity-list borrowed-books-page">
      <div className="main-content-header">
        <div className="right-content">
          <h2>{title}</h2>
        </div>
        <div className="center-content">
          <input className="parity-search-input" type="search" aria-label="جستجو" placeholder="جستجو" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="left-content">
          {/* Testaria borrowed_book_list exposes Excel only (no print control). */}
          <ParityReport
            title="لیست قرض گیرنده ها"
            print={false}
            columns={columns}
            loadRows={() => allReportPages(route)}
            disabled={denied || loading}
          />
        </div>
      </div>
      <div className="main-content-body">
        {denied ? <AccessDeniedResponse /> : loading ? <LoadingState /> : error ? (
          <EmptyState title={error} action={<button type="button" onClick={refresh}>بارگیری مجدد</button>} />
        ) : !body.data?.length ? <EmptyState title="اطلاعات وجود ندارد." /> : (
          <table>
            <thead>
              <tr>
                {columns.map((c, i) => (
                  <th key={i}>
                    <button type="button" onClick={() => { setSort({ orderBy: c.path || 'id', order: sort.order === 'asc' ? 'desc' : 'asc' }); setPage(1); }}>
                      {c.label} ↕
                    </button>
                  </th>
                ))}
                <th className="option-head" aria-label="عملیات" />
              </tr>
            </thead>
            <tbody>
              {body.data.map((row, i) => (
                <tr key={row.id} className={i % 2 ? 'odd' : undefined}>
                  {columns.map((c, j) => (
                    <td key={j}>{c.value ? c.value(row, (page - 1) * 40 + i) : (row[c.path] ?? '—')}</td>
                  ))}
                  <td>
                    <div className="parity-row-actions">
                      {Number(row.returned) !== 1 && can('view-book-list', 'add-book') && (
                        <button
                          type="button"
                          className="parity-icon-action"
                          title="برگشت کتاب"
                          aria-label="برگشت کتاب"
                          disabled={returningId === row.id}
                          onClick={() => returnBook(row)}
                        >
                          <ReturnIcon />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {message && <p role="status">{message}</p>}
      </div>
      <div className="main-content-footer parity-actions">
        <button className="mis-button is-secondary" disabled={loading || page <= 1} onClick={() => setPage(page - 1)}>صفحه قبلی</button>
        <span>{body.totalRows ? (page - 1) * 40 + 1 : 0} - {Math.min(page * 40, body.totalRows || 0)} از {body.totalRows || 0}</span>
        <button className="mis-button is-secondary" disabled={loading || page >= Number(body.totalPages || 1)} onClick={() => setPage(page + 1)}>صفحه بعدی</button>
      </div>
    </div>
  );
}
